import React, { useContext, useEffect, useMemo, useState } from "react";
import { CreditCard, Building2, Check, X, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "../../../components/ui/dialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useNavigate } from "react-router";
// import PaymentSuccess from "./PaymentSuccess";
import { useStripe } from "@stripe/react-stripe-js";
import AuthContext from "../../../hooks/authContext";
import { getBankAccountDetails, getCardDetails, saveCardDetails } from "../../../services/apiservices/users";

const PaymentMethodsList = ({
  open,
  onClose,
  onConfirm,
  paymentMethods,
  planInfo,
  temptPaymentData,
  setTemptPaymentData,
  setSelectedPaymentMethodId,
  selectedPaymentmethodId,
  setIsProcessing,
  isProcessing,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  setPaymentSuccess
}) => {
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const stripe = useStripe();
  const [showAllPayments, setShowAllPayments] = useState(false);

  const initialTemptState = {
    open: false,
    newPriceId: null,
    newAmounts: null,
    data: null,
    daysUsed: 0,
    daysRemaining: 0,
    amountToPay: 0,
    finalAmountDue: 0,
    creditAmounts: 0,
    planUnitAmount: 0,
    billingLabel: "",
    plansDetail: null,
  };

  const { cards = [], defaultPaymentMethodId } = paymentMethods || {};

  const cardMethods = cards?.filter((pm) => pm.type === "card");
  const bankMethods = cards?.filter((pm) => pm.type === "us_bank_account");

  const handlePaymentMethodSelect = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setTemptPaymentData(initialTemptState)
    setSelectedPaymentMethodId(paymentMethod);

    onConfirm();
  };

  const handleAddNewPayment = () => {
    navigate("/account/companySubscription", {
      state: { intent: "ADD_PAYMENT" }
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleConfirm = async () => {
    if (!selectedPaymentMethod) return;

    setIsProcessing(true);
    setSelectedPaymentMethodId(selectedPaymentMethod);
    onConfirm();
  };

  useEffect(() => {
    if (!paymentMethods || !cardMethods || !bankMethods) return;

    let defaultMethod = cardMethods?.find((c) => c.id === defaultPaymentMethodId) || bankMethods?.find((b) => b.id === defaultPaymentMethodId) || cards[0] || bankMethods[0];

    if (defaultMethod) {
      setIsProcessing(true);
      setSelectedPaymentMethodId(defaultMethod?.id);
      setSelectedPaymentMethod(defaultMethod);

      onConfirm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dependencies are stable or intentionally excluded

  const MAX_VISIBLE = 5;

  const combinedMethods = useMemo(() => {
    const combined = [...(cardMethods || []), ...(bankMethods || [])];

    return combined?.sort((a, b) => {
      if (a.id === defaultPaymentMethodId) return -1;
      if (b.id === defaultPaymentMethodId) return 1;
      return 0;
    });
  }, [cardMethods, bankMethods, defaultPaymentMethodId]);

  const visibleMethods = showAllPayments ? combinedMethods : combinedMethods?.slice(0, MAX_VISIBLE);

  const getCardBrandIcon = (brand) => {
    const brandName = brand?.toLowerCase();

    switch (brandName) {
      case "visa":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#1434CB" />
            <path
              d="M20.5 11.5L18 20.5H15.5L13 11.5H15.5L17 18L18.5 11.5H20.5Z"
              fill="white"
            />
            <path d="M24 11.5H21.5L20 20.5H22.5L24 11.5Z" fill="white" />
            <path
              d="M28.5 11.5C27.5 11.5 26.5 12 26 13L27 14C27.3 13.5 27.8 13.2 28.5 13.2C29.2 13.2 29.5 13.5 29.5 14V14.5C29 14.3 28.5 14.2 28 14.2C26.5 14.2 25.5 15 25.5 16.5C25.5 18 26.5 18.8 28 18.8C28.8 18.8 29.3 18.5 29.7 18L30 18.8H32V14C32 12.3 31 11.5 28.5 11.5ZM28.5 17C28 17 27.7 16.7 27.7 16.3C27.7 15.8 28 15.5 28.8 15.5C29.2 15.5 29.5 15.6 29.8 15.7V16.5C29.5 16.8 29 17 28.5 17Z"
              fill="white"
            />
          </svg>
        );
      case "mastercard":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#EB001B" />
            <circle cx="18" cy="16" r="10" fill="#FF5F00" />
            <circle cx="30" cy="16" r="10" fill="#F79E1B" />
            <path
              d="M24 10C22 11.5 20.5 13.5 20.5 16C20.5 18.5 22 20.5 24 22C26 20.5 27.5 18.5 27.5 16C27.5 13.5 26 11.5 24 10Z"
              fill="#FF5F00"
            />
          </svg>
        );
      case "amex":
      case "american express":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#006FCF" />
            <path
              d="M15 12H18L16.5 18L15 12ZM20 12L18.5 18H21.5L23 12H20ZM25 12H28V18H25V12Z"
              fill="white"
            />
          </svg>
        );
      case "discover":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#FF6000" />
            <circle cx="38" cy="16" r="8" fill="#FFCB05" />
            <path
              d="M12 12H15V20H12V12ZM17 12H20C22 12 23 13 23 15C23 17 22 18 20 18H17V12Z"
              fill="white"
            />
          </svg>
        );
      case "diners":
      case "diners club":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#0079BE" />
            <circle
              cx="18"
              cy="16"
              r="8"
              fill="none"
              stroke="white"
              strokeWidth="2"
            />
            <circle
              cx="30"
              cy="16"
              r="8"
              fill="none"
              stroke="white"
              strokeWidth="2"
            />
          </svg>
        );
      case "jcb":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#0E4C96" />
            <path
              d="M15 12H18V20H15V12ZM20 12H23V18C23 19 23.5 20 25 20H27V12H30V20C30 22 28.5 23 26 23H23C20.5 23 20 22 20 20V12Z"
              fill="white"
            />
          </svg>
        );
      case "unionpay":
        return (
          <svg className="w-8 h-8" viewBox="0 0 48 32" fill="none">
            <rect width="48" height="32" rx="4" fill="#002663" />
            <path
              d="M12 12H18V14H14V18H18V20H12V12ZM20 12H26V20H24V14H22V20H20V12Z"
              fill="#E21836"
            />
          </svg>
        );
      default:
        return (
          <CreditCard className="w-8 h-8 text-gray-600 dark:text-gray-400" />
        );
    }
  };

  const formatExpiryDate = (month, year) => {
    return `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
  };

  const formatStripeAmount = (amount) => {
    return `$${(amount / 100).toFixed(0)}`;
  };

  const handlePay = async () => {
    try {
      setIsProcessing(true);

      if (!temptPaymentData?.data?.clientSecret || !selectedPaymentMethod) {
        setIsProcessing(false);
        return;
      }

      let result;

      if (selectedPaymentMethod?.type === "card") {
        result = await stripe.confirmCardPayment(
          temptPaymentData.data.clientSecret,
          {
            payment_method: selectedPaymentMethod?.id || selectedPaymentmethodId,
          }
        );
      } else {
        result = await stripe.confirmUsBankAccountPayment(
          temptPaymentData.data.clientSecret,
          {
            payment_method: selectedPaymentMethod?.id || selectedPaymentmethodId,
          }
        );
      }

      if (result.error) {
        console.error(result.error);
        return;
      }

      const paymentIntent = result.paymentIntent;
      const paymentMethodId = paymentIntent.payment_method;

      const paymentMethodType =
        selectedPaymentMethod?.type ??
        paymentIntent.payment_method_types?.[0];

      const details =
        paymentMethodType === "card"
          ? await getCardDetails(paymentIntent.id)
          : await getBankAccountDetails(paymentIntent);

      const cardOrBankDetails = {
        ...details,
        customer: auth?.userProfile?.customer,
      };

      const formData = {
        email: auth?.userProfile?.email,
        name: auth?.userProfile?.firstName,
      };

      const subscriptionId =
        auth?.userProfile?.subscriptionDetails?.id ||
        auth?.userProfile?.subscriptions?.data?.[0]?.id;

      await saveCardDetails(
        cardOrBankDetails,
        formData,
        subscriptionId,
        temptPaymentData?.newPriceId,
        temptPaymentData?.newAmounts,
        paymentMethodId,
        paymentMethodType
      );

      await delay(3000);

      setPaymentSuccess(true);
      onClose?.();
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <div className="fixed inset-0 flex items-center justify-center z-50 p-16">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <DialogContent
            className="max-w-2xl p-0 gap-0 overflow-hidden bg-white dark:bg-gray-900"
            closeButton={false}
            closeOnOutsideClick={false}
          >
            <DialogHeader>
              <DialogTitle>
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-24">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white">
                      {temptPaymentData?.open
                        ? "Confirm Payment"
                        : "Choose Payment Method"}
                    </h2>
                    <button
                      onClick={onClose}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-8 transition-colors"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>

            {/* {!temptPaymentData?.open && (
              <div className="overflow-y-auto max-h-[calc(90vh-160px)]">
                {planInfo && (
                  <div className="bg-green-50 dark:bg-gray-800 border-l-4 border-green-600 p-24 m-24">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          {planInfo.planName}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {planInfo.description}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600 dark:text-green-500">
                          {formatStripeAmount(planInfo.amount)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {planInfo.isYearly ? "per year" : "per month"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-24 space-y-6">
                  {cardMethods?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-12 uppercase tracking-wide">
                        Credit & Debit Cards
                      </h3>
                      <div className="space-y-3">
                        {cardMethods?.map((method) => {
                          const isDefault =
                            method.id === defaultPaymentMethodId;
                          const isSelected =
                            selectedPaymentMethod?.id === method.id;
                          const card = method.card;
                          return (
                            <div
                              key={method.id}
                              onClick={() => handlePaymentMethodSelect(method)}
                              className={`border-2 rounded-xl p-16 cursor-pointer transition-all ${
                                isSelected
                                  ? "border-green-600 bg-green-50 dark:bg-green-900"
                                  : "border-gray-200 dark:border-gray-700 hover:border-green-300"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                    {getCardBrandIcon(card.brand)}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-semibold text-gray-900 dark:text-white capitalize">
                                        {card.brand} •••• {card.last4}
                                      </p>
                                      {isDefault && (
                                        <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded-full font-medium">
                                          Default
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      Expires{" "}
                                      {formatExpiryDate(
                                        card.exp_month,
                                        card.exp_year
                                      )}
                                    </p>
                                    {method.billing_details?.name && (
                                      <p className="text-xs text-gray-400 dark:text-gray-500">
                                        {method.billing_details.name}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                    <Check className="text-white" size={16} />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {bankMethods?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-12 uppercase tracking-wide">
                        Bank Accounts
                      </h3>
                      <div className="space-y-3">
                        {bankMethods?.map((method) => {
                          const isDefault =
                            method.id === defaultPaymentMethodId;
                          const isSelected =
                            selectedPaymentMethod?.id === method.id;
                          const bank = method.us_bank_account;

                          return (
                            <div
                              key={method.id}
                              onClick={() => handlePaymentMethodSelect(method)}
                              className={`border-2 rounded-xl p-16 cursor-pointer transition-all ${
                                isSelected
                                  ? "border-green-600 bg-green-50 dark:bg-green-900"
                                  : "border-gray-200 dark:border-gray-700 hover:border-green-300"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div
                                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                      isSelected
                                        ? "bg-green-600"
                                        : "bg-gray-100 dark:bg-gray-800"
                                    }`}
                                  >
                                    <Building2
                                      className={
                                        isSelected
                                          ? "text-white"
                                          : "text-gray-600 dark:text-gray-400"
                                      }
                                      size={24}
                                    />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-semibold text-gray-900 dark:text-white">
                                        {bank?.bank_name}
                                      </p>
                                      {isDefault && (
                                        <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded-full font-medium">
                                          Default
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                                      {bank?.account_type} •••• {bank?.last4}
                                    </p>
                                    {method.billing_details?.name && (
                                      <p className="text-xs text-gray-400 dark:text-gray-500">
                                        {method?.billing_details?.name}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                    <Check className="text-white" size={16} />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleAddNewPayment}
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-16 text-gray-600 dark:text-gray-400 hover:border-green-500 transition-colors flex items-center justify-center space-x-2 group"
                  >
                    <Plus size={20} />
                    <span className="font-medium">Add New Payment Method</span>
                  </button>
                  {cardMethods.length === 0 && bankMethods.length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-16">
                        <CreditCard className="text-gray-400" size={32} />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-16">
                        No payment methods found
                      </p>
                      <button
                        onClick={handleAddNewPayment}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                      >
                        Add Payment Method
                      </button>
                    </div>
                  )}
                </div>

                {(cardMethods.length > 0 || bankMethods.length > 0) && (
                  <div className="border-t border-gray-200 dark:border-gray-800 p-24 bg-gray-50 dark:bg-gray-800">
                    <div className="flex gap-3">
                      <button
                        onClick={onClose}
                        className="flex-1 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>

                      <button
                        onClick={handleConfirm}
                        disabled={!selectedPaymentMethod}
                        className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl transition-all disabled:opacity-50"
                      >
                       {isProcessing ? "Processing..." :  "Continue to Confirm"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )} */}

            {temptPaymentData?.open ? (
              <div className="p-24 overflow-y-auto max-h-[calc(90vh-200px)]">
                <div className="rounded-xl bg-white dark:bg-[#0F1729] shadow-sm p-24 space-y-6">
                  <div className="flex items-start justify-between border-b pb-16">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-white">
                        New Plan
                      </p>
                      <h3 className="text-lg font-semibold dark:text-white">
                        {planInfo?.planName}
                      </h3>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold dark:text-white">
                        {formatStripeAmount(planInfo?.amount)}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-white">
                        {temptPaymentData?.billingLabel}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-md bg-gray-50 dark:bg-[#071025] p-16 text-sm">
                    <p className="dark:text-white">
                      Today you'll get a pro-rated charge based on remaining
                      days.
                    </p>

                    <div className="mt-12 grid grid-cols-2 gap-2">
                      <div className="dark:text-white">Days Used</div>
                      <div className="text-right font-medium dark:text-white">
                        {temptPaymentData?.daysUsed}
                      </div>
                      <div className="dark:text-white">Days Remaining</div>
                      <div className="text-right font-medium dark:text-white">
                        {temptPaymentData?.data?.daysRemaining}
                      </div>
                      <div className="dark:text-white">Credits</div>
                      <div className="text-right font-medium dark:text-white">
                        {formatStripeAmount(temptPaymentData?.creditAmounts)}
                      </div>
                      <div className="dark:text-white">New Plan</div>
                      <div className="text-right font-medium dark:text-white">
                        {formatStripeAmount(planInfo?.amount)}
                      </div>
                      <div className="dark:text-white">Amount Due Today</div>
                      <div className="text-right font-medium dark:text-white">
                        {formatStripeAmount(temptPaymentData?.amountToPay > 0 ? temptPaymentData?.amountToPay : temptPaymentData?.newAmounts)}
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500 mb-8 dark:text-white">
                      Payment Method
                    </p>
                    <div className="rounded-lg border p-16 flex items-center justify-between">
                      <div className="dark:text-white">
                        <p className="font-medium">
                          {selectedPaymentMethod?.type === "us_bank_account"
                            ? "Bank Account"
                            : "Card"}
                        </p>

                        <p className="text-xs text-slate-500 mv-4">
                          {selectedPaymentMethod?.type === "card" && (
                            <>
                              {selectedPaymentMethod.card.brand.toUpperCase()} •••• {selectedPaymentMethod.card.last4}
                            </>
                          )}
                          {selectedPaymentMethod?.type === "us_bank_account" && (
                            <>
                              {selectedPaymentMethod.us_bank_account.bank_name} •••• {selectedPaymentMethod.us_bank_account.last4}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  {visibleMethods?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-12 uppercase tracking-wide">
                        Payment Methods
                      </h3>

                      <div className="space-y-3">
                        {visibleMethods?.map((method) => {
                          const isDefault = method?.id === defaultPaymentMethodId;
                          const isSelected = selectedPaymentMethod?.id === method?.id;
                          const isCard = !!method?.card;
                          const card = method?.card;
                          const bank = method?.us_bank_account;

                          return (
                            <div
                              key={method?.id}
                              onClick={() => handlePaymentMethodSelect(method)}
                              className={`border-2 rounded-xl p-16 cursor-pointer transition-all ${
                                isSelected
                                  ? "border-green-600 bg-green-50 dark:bg-green-900"
                                  : "border-gray-200 dark:border-gray-700 hover:border-green-300"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                    {isCard ? (
                                      getCardBrandIcon(card.brand)
                                    ) : (
                                      <Building2
                                        className="text-gray-600 dark:text-gray-400"
                                        size={24}
                                      />
                                    )}
                                  </div>

                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-semibold text-gray-900 dark:text-white">
                                        {isCard
                                          ? `${card.brand} •••• ${card.last4}`
                                          : bank?.bank_name}
                                      </p>

                                      {isDefault && (
                                        <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded-full font-medium">
                                          Default
                                        </span>
                                      )}
                                    </div>

                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      {isCard
                                        ? `Expires ${formatExpiryDate(
                                            card.exp_month,
                                            card.exp_year
                                          )}`
                                        : `${bank?.account_type} •••• ${bank?.last4}`}
                                    </p>
                                  </div>
                                </div>

                                {isSelected && (
                                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                    <Check className="text-white" size={16} />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {combinedMethods?.length > MAX_VISIBLE && (
                        <button
                          onClick={() => setShowAllPayments((prev) => !prev)}
                          className="mt-12 text-sm font-medium text-green-600 hover:underline"
                        >
                          {showAllPayments
                            ? "Show less payment methods"
                            : "Show more payment methods"}
                        </button>
                      )}

                      <button
                        onClick={handleAddNewPayment}
                        className="w-full mt-12 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-16 text-gray-600 dark:text-gray-400 hover:border-green-500 transition-colors flex items-center justify-center space-x-2 group"
                      >
                        <Plus size={20} />
                        <span className="font-medium">Add New Payment Method</span>
                      </button>
                    </div>
                  )}

                  {cardMethods?.length === 0 && bankMethods?.length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-16">
                        <CreditCard className="text-gray-400" size={32} />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-16">
                        No payment methods found
                      </p>
                      <button
                        onClick={handleAddNewPayment}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                      >
                        Add Payment Method
                      </button>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-base font-semibold pt-8">
                    <div className="dark:text-white">Total Due Now</div>
                    <div className="dark:text-white">
                      {formatStripeAmount(
                        temptPaymentData?.amountToPay > 0
                          ? temptPaymentData?.amountToPay
                          : temptPaymentData?.newAmounts
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-24">
                  <button
                    disabled={isProcessing}
                    // onClick={() => setTemptPaymentData(initialTemptState)}
                    onClick={onClose}
                    className="flex-1 border py-3 rounded-lg dark:text-white"
                  >
                    Back
                  </button>

                  <button
                    onClick={handlePay}
                    disabled={isProcessing}
                    className={`flex-1 rounded-lg py-3 font-semibold ${
                      !isProcessing
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {isProcessing
                      ? "Processing..."
                      : `Pay ${formatStripeAmount(
                          temptPaymentData?.amountToPay > 0
                            ? temptPaymentData?.amountToPay
                            : temptPaymentData?.newAmounts
                        )}`}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-300">
                <svg
                  className="animate-spin h-12 w-12 mb-4 text-green-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  ></path>
                </svg>
                <p className="text-sm font-medium dark:text-white">Loading payment details...</p>
              </div>
            )}
          </DialogContent>
        </div>
      </div>
    </Dialog>
  );
};

export default PaymentMethodsList;
